import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError } from '@shared/api';

/**
 * Which form field a backend field name maps to. An array is the identity
 * mapping (wire name === form name); a record handles the asymmetric cases —
 * the profile PUT body says `productionPhotoMediaIds` where the form field is
 * `productionPhotos`.
 */
export type FieldNameMap = readonly string[] | Record<string, string>;

interface ApplyFieldErrorsOptions {
  /**
   * Fields this form owns. Used when the backend reports a validation failure
   * without an `errors[]` array: we recover the field from the `detail` string,
   * but only when it matches one of these — so a generic message like
   * "must be a valid URL" can't be mis-attributed and silently swallowed.
   */
  fields?: FieldNameMap;
}

function resolve(map: FieldNameMap, wireName: string): string | null {
  if (Array.isArray(map)) return map.includes(wireName) ? wireName : null;
  return (map as Record<string, string>)[wireName] ?? null;
}

/**
 * Newer backend validation reports the offending field inside `detail`
 * ("productionPhotoMediaIds must contain at most 20 items") rather than in the
 * Bean-Validation `errors[]` array. Pull the leading identifier back out.
 */
function fieldFromDetail(
  detail: string | undefined,
  map: FieldNameMap,
): string | null {
  if (!detail) return null;
  const leading = /^([A-Za-z][A-Za-z0-9_.[\]]*)\b/.exec(detail.trim())?.[1];
  if (!leading) return null;
  // Tolerate an indexed path like `exportCountries[3]` — anchor on the root.
  return resolve(map, leading.replace(/\[\d+\]$/, ''));
}

export function applyApiFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  options: ApplyFieldErrorsOptions = {},
): boolean {
  if (!(error instanceof ApiError)) return false;

  const { fields } = options;

  // Preferred path: an explicit errors[] array from the backend.
  const entries = Object.entries(error.fieldErrors);
  if (entries.length > 0) {
    for (const [wireName, message] of entries) {
      const formField = fields ? resolve(fields, wireName) : wireName;
      setError((formField ?? wireName) as Path<T>, { type: 'server', message });
    }
    return true;
  }

  // Fallback: field name embedded in `detail`.
  if (fields) {
    const formField = fieldFromDetail(error.problem.detail, fields);
    if (formField) {
      setError(formField as Path<T>, { type: 'server', message: error.message });
      return true;
    }
  }

  return false;
}
